import { useEffect } from 'react';
import {
  usePracticeLabs,
  useProject,
  useTasksFromProjectId,
} from '@witmetrics/data-store';
import { useAppState } from '@/providers/AppStateProvider';
import { usePath } from '@/hooks/usePath';
import { useTab } from '@/hooks/useTab';
import TabsBlock, { type TabChild } from '@/components/TabsBlock';
import OverviewTab from '../tabs/OverviewTab';
import TasksTab from '../tabs/TasksTab';
import DocumentsTab from '../tabs/DocumentsTab';
import OrdersTab from '../tabs/OrdersTab';
import type { CaseOverlayTab } from '../types';
import { classes, getDefaultTab } from './utils';

export type TabsColumnProps = {
  isFetchingData: boolean;
  isFetchingFiles: boolean;
};

export default function TabsColumn({
  isFetchingData,
  isFetchingFiles,
}: TabsColumnProps) {
  const { activeProjectId } = useAppState();
  const { searchParams, pushParams } = usePath();
  const practiceLabs = usePracticeLabs();
  const project = useProject(activeProjectId);
  const tasks = useTasksFromProjectId(activeProjectId);
  const [defaultTab, setDefaultTab] = useTab<CaseOverlayTab>(
    getDefaultTab(searchParams)
  );

  useEffect(() => {
    const tab = searchParams.get('caseOverlayTab');
    if (tab !== null && tab !== defaultTab) {
      handleSelectTab(tab as CaseOverlayTab);
    }
  }, [searchParams]);

  const handleSelectTab = (tab: CaseOverlayTab) => {
    setDefaultTab(tab);
    pushParams({ caseOverlayTab: tab });
  };

  const renderTasksAdornment = () => {
    const openTasks = tasks.filter(({ task }) => !task.isResolved).length;
    if (openTasks === 0) return null;
    return <div className={classes.adornment}>{openTasks}</div>;
  };

  if (!project) return null;

  let tabs: TabChild[] = [
    <OverviewTab
      key="overview"
      isFetchingData={isFetchingData}
      isFetchingFiles={isFetchingFiles}
      name="overview"
      label="Overview"
    />,
    <DocumentsTab
      key="documents"
      isFetchingData={isFetchingData}
      name="documents"
      label="Forms & Documents"
    />,
    <TasksTab
      key="tasks"
      isFetchingData={isFetchingData}
      name="tasks"
      label="Tasks"
      adornment={renderTasksAdornment()}
    />,
  ];

  if (practiceLabs.length > 0) {
    tabs.push(
      <OrdersTab
        key="orders"
        isFetchingData={isFetchingData}
        name="orders"
        label="Orders"
      />
    );
  }

  return (
    <div className={classes.column}>
      <TabsBlock<CaseOverlayTab[]>
        className={classes.tabsBlock}
        name="case-info"
        bodyClassName={classes.tabBody}
        defaultTab={defaultTab}
        onChange={handleSelectTab}>
        {tabs}
      </TabsBlock>
    </div>
  );
}
